import { useState, useEffect, useRef } from 'react'
import {
  supabase,
  publicSupabase,
  supabaseRPC,
  getCurrentHost,
  getSlugFromPath,
  getUserSubdomainFromPath,
  getUTMParameters,
  getFacebookParameters,
  getReferrer,
  shouldSkipSession,
  getDeviceType,
  getBrowser,
  getOS,
  getGeoLocation,
  getVisitorIP,
  getQuestionId,
  getPageTrackingId,
  nowGmtMinus3ISO,
  addUTMsToUrl,
  addTrackingParamsToUrl,
  PublishedQuiz,
  QuizSession
} from '../lib/supabase'
import { Page } from '../../types'
import { logger } from '../lib/logger'

interface UseQuizViewerReturn {
  quiz: PublishedQuiz | null
  session: QuizSession | null
  loading: boolean
  error: string | null
  currentStep: number
  answers: Record<string, any>
  leadData: Record<string, any>
  pageHistory: Array<{
    pageId: string
    pageIndex: number
    viewedAt: string
    completedAt?: string
    timeSpent?: number
  }>
  submitAnswer: (questionId: string, answer: any) => Promise<void>
  nextStep: () => void
  prevStep: () => void
  submitLead: (leadData: Record<string, any>) => Promise<void>
  completeQuiz: (formData?: Record<string, string>) => Promise<void>
  addUTMsToRedirectUrl: (url: string) => string
  trackExternalClick: () => Promise<void>
  onQuizComplete?: (formData: Record<string, string>) => void
  score: number
  awardedSteps: number[]
  saveScore: (newScore: number, newAwardedSteps: number[]) => Promise<void>
  isRecoveredCompleted: boolean
}


export function useQuizViewer(onQuizComplete?: (formData: Record<string, string>) => void): UseQuizViewerReturn {
  const [quiz, setQuiz] = useState<PublishedQuiz | null>(null)
  const [session, setSession] = useState<QuizSession | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [currentStep, setCurrentStep] = useState(0)
  const [isRecoveredCompleted, setIsRecoveredCompleted] = useState(false)
  const [answers, setAnswers] = useState<Record<string, any>>({}) // Estado interno com IDs para UI
  const [answersForDB, setAnswersForDB] = useState<Record<string, any>>({}) // Estado completo (texto + IDs) para banco
  const [leadData, setLeadData] = useState<Record<string, any>>({})
  const [score, setScore] = useState(0)
  const [awardedSteps, setAwardedSteps] = useState<number[]>([])
  const [utmParams, setUtmParams] = useState<{
    source?: string
    medium?: string
    campaign?: string
    term?: string
    content?: string
  }>({}) // Armazenar UTMs para usar em redirecionamentos
  const [facebookParams, setFacebookParams] = useState<{
    fbp?: string
    fbc?: string
    fbclid?: string
  }>({}) // Armazenar parâmetros do Facebook para usar em redirecionamentos
  const [pageHistory, setPageHistory] = useState<Array<{
    pageId: string
    pageIndex: number
    viewedAt: string
    completedAt?: string
    timeSpent?: number
  }>>([])

  const initialized = useRef(false)
  const pageViewStartTime = useRef<number>(Date.now())
  const pageHistoryRef = useRef<typeof pageHistory>([])
  const sessionStartTimeRef = useRef<number | null>(null)

  const isSavingHistory = useRef(false)
  const pendingHistorySave = useRef<typeof pageHistory | null>(null)
  const pendingSaveResolvers = useRef<(() => void)[]>([])

  // Função para processar a fila de salvamento de histórico
  const processSaveQueue = async (sessionId: string) => {
    if (isSavingHistory.current) return

    try {
      isSavingHistory.current = true

      while (pendingHistorySave.current) {
        // Capturar o estado atual para salvar
        const historyToSave = pendingHistorySave.current
        // Capturar os resolvers que estão esperando por ESTE save (ou acumulados)
        const resolversToNotify = [...pendingSaveResolvers.current]

        // Limpar estado pendente para permitir que novos entrem durante o await
        pendingHistorySave.current = null
        pendingSaveResolvers.current = []

        logger.log('💾 [processSaveQueue] Salvando lote de histórico...', historyToSave.length, 'entradas')

        // CORREÇÃO: Buscar session_data ATUALIZADO do banco antes de atualizar
        // Isso evita sobrescrever com dados desatualizados do estado React
        const { data: currentSession, error: fetchError } = await publicSupabase
          .from('quiz_sessions')
          .select('session_data')
          .eq('id', sessionId)
          .single()

        if (fetchError) {
          logger.error('❌ [processSaveQueue] Erro ao buscar session_data atual:', fetchError)
          // Notificar mesmo com erro para não bloquear
          resolversToNotify.forEach(resolve => resolve())
          continue
        }

        // Usar os dados do banco como base, não o estado React
        const currentSessionData = currentSession?.session_data || {}
        const updatedSessionData = {
          ...currentSessionData,
          page_history: historyToSave
        }

        logger.log('💾 [processSaveQueue] session_data atual tinha:',
          currentSessionData.page_history?.length || 0, 'páginas. Atualizando para:', historyToSave.length)

        const { error: updateError } = await publicSupabase
          .from('quiz_sessions')
          .update({ session_data: updatedSessionData })
          .eq('id', sessionId)

        if (updateError) {
          logger.error('❌ [processSaveQueue] Erro ao salvar:', updateError)
        } else {
          logger.log('✅ [processSaveQueue] Sucesso')
        }

        // Notificar todos que estavam esperando que O save aconteceu
        resolversToNotify.forEach(resolve => resolve())
      }
    } catch (err) {
      logger.error('❌ [processSaveQueue] Erro inesperado:', err)
    } finally {
      isSavingHistory.current = false
      // Se sobrou algo pendente (novo save entrou enquanto salvava), processa novamente
      if (pendingHistorySave.current) {
        processSaveQueue(sessionId)
      }
    }
  }

  // Função para salvar page_history no banco (com fila e Promise)
  const savePageHistoryToDatabase = (updatedHistory: typeof pageHistory, sessionId?: string): Promise<void> => {
    const targetSessionId = sessionId || session?.id
    if (!targetSessionId) {
      logger.warn('⚠️ [savePageHistory] Sem sessão ativa, pulando salvamento')
      return Promise.resolve()
    }

    // Atualizar o estado pendente com a versão MAIS RECENTE
    pendingHistorySave.current = updatedHistory

    // Retornar uma promessa que resolve quando ESSE save for processado
    return new Promise<void>((resolve) => {
      pendingSaveResolvers.current.push(resolve)
      processSaveQueue(targetSessionId)
    })
  }

  // Função para registrar visualização de página
  const trackPageView = async (page: Page, pageIndex: number, sessionId?: string) => {
    const pageId = getPageTrackingId(page) // Usa uniqueId
    const now = nowGmtMinus3ISO() // Usar timezone GMT-3

    logger.log('👁️ [trackPageView] INÍCIO:', { pageId, pageIndex, sessionId, hasSession: !!session?.id })

    // Calcular histórico atualizado usando o ref (sempre atualizado)
    const currentHistory = pageHistoryRef.current
    logger.log('👁️ [trackPageView] Estado atual (ref):', currentHistory.length, 'entradas:', currentHistory.map(p => ({ pageId: p.pageId, pageIndex: p.pageIndex })))

    // Verificar se já existe registro para esta página
    const existingIndex = currentHistory.findIndex(p => p.pageId === pageId)
    let updatedHistory: typeof pageHistory

    if (existingIndex >= 0) {
      // Atualizar registro existente (caso volte para a página)
      logger.log('👁️ [trackPageView] Página já existe no histórico, atualizando...')
      updatedHistory = [...currentHistory]
      updatedHistory[existingIndex] = {
        ...updatedHistory[existingIndex],
        viewedAt: updatedHistory[existingIndex].viewedAt || now, // Manter primeira visualização
        pageIndex // Atualizar índice caso tenha mudado
      }
      logger.log('👁️ [trackPageView] Histórico atualizado:', updatedHistory.length, 'entradas')
    } else {
      // Adicionar novo registro
      logger.log('👁️ [trackPageView] Nova página, adicionando ao histórico...')
      const newEntry = {
        pageId,
        pageIndex,
        viewedAt: now
      }
      updatedHistory = [...currentHistory, newEntry]
      logger.log('👁️ [trackPageView] Histórico após adicionar:', updatedHistory.length, 'entradas:', updatedHistory.map(p => ({ pageId: p.pageId, pageIndex: p.pageIndex })))
    }

    // Atualizar ref e estado
    pageHistoryRef.current = updatedHistory
    setPageHistory(updatedHistory)

    pageViewStartTime.current = Date.now()
    logger.log('👁️ [trackPageView] Página visualizada:', { pageId, pageIndex, uniqueId: page.uniqueId, viewedAt: now, sessionId })

    // Salvar IMEDIATAMENTE no banco (passar sessionId se fornecido)
    logger.log('👁️ [trackPageView] Chamando savePageHistoryToDatabase com', updatedHistory.length, 'entradas')
    await savePageHistoryToDatabase(updatedHistory, sessionId)
  }

  // Função para registrar conclusão de página
  const trackPageCompletion = async (page: Page, sessionId?: string) => {
    const pageId = getPageTrackingId(page) // Usa uniqueId
    const now = nowGmtMinus3ISO() // Usar timezone GMT-3
    const timeSpent = Math.floor((Date.now() - pageViewStartTime.current) / 1000)

    logger.log('✅ [trackPageCompletion] INÍCIO:', { pageId, sessionId, timeSpent, hasSession: !!session?.id })

    const currentHistory = pageHistoryRef.current
    logger.log('✅ [trackPageCompletion] Estado atual (ref):', currentHistory.length, 'entradas:', currentHistory.map(p => ({ pageId: p.pageId, pageIndex: p.pageIndex })))

    const existingIndex = currentHistory.findIndex(p => p.pageId === pageId)
    if (existingIndex >= 0) {
      logger.log('✅ [trackPageCompletion] Página encontrada no índice:', existingIndex, 'atualizando...')
      const updatedHistory = [...currentHistory]
      updatedHistory[existingIndex] = {
        ...updatedHistory[existingIndex],
        completedAt: now,
        timeSpent: Math.max(timeSpent, updatedHistory[existingIndex].timeSpent || 0) // Manter maior tempo
      }
      pageHistoryRef.current = updatedHistory
      setPageHistory(updatedHistory)

      logger.log('✅ [trackPageCompletion] Histórico atualizado:', updatedHistory.length, 'entradas')
      logger.log('✅ [trackPageCompletion] Página concluída:', { pageId, timeSpent, uniqueId: page.uniqueId, completedAt: now, sessionId })
      logger.log('✅ [trackPageCompletion] Chamando savePageHistoryToDatabase com', updatedHistory.length, 'entradas')
      await savePageHistoryToDatabase(updatedHistory, sessionId)
    } else {
      logger.warn('⚠️ [trackPageCompletion] Página NÃO encontrada no histórico! pageId:', pageId)
      logger.warn('⚠️ [trackPageCompletion] Histórico atual:', currentHistory.map(p => ({ pageId: p.pageId, pageIndex: p.pageIndex })))
    }
  }

  // Função para atualizar dados adicionais em background (não bloqueante)
  const updateSessionWithAdditionalData = async (
    sessionId: string,
    currentPath: string,
    utmParams: any,
    referrer: string | undefined
  ) => {
    try {
      logger.log('🔄 [background] Iniciando atualização em background...')

      // Verificar se coleta de geodata está habilitada
      const enableGeodata = import.meta.env.VITE_ENABLE_GEODATA !== 'false'
      logger.log('🌍 [background] Coleta de geodata:', enableGeodata ? 'HABILITADA' : 'DESABILITADA')

      // Capturar informações do dispositivo (síncronas e rápidas)
      const deviceType = getDeviceType()
      const browser = getBrowser()
      const os = getOS()

      logger.log('📱 [background] Informações básicas:', { deviceType, browser, os })

      // Salvar dados básicos primeiro (sempre funciona)
      const updateData: any = {
        path: currentPath,
        user_agent: navigator.userAgent,
        device_type: deviceType,
        browser: browser,
        os: os,
        utm_source: utmParams.source || null,
        utm_medium: utmParams.medium || null,
        utm_campaign: utmParams.campaign || null,
        utm_term: utmParams.term || null,
        utm_content: utmParams.content || null,
        referrer: referrer || null
      }

      const { error: updateError, count } = await publicSupabase
        .from('quiz_sessions')
        .update(updateData, { count: 'exact' })
        .eq('id', sessionId)

      if (updateError) {
        logger.error('❌ [background] Erro ao salvar dados básicos:', updateError)
      } else if (count === 0) {
        logger.warn('⚠️ [background] Update executado com sucesso mas 0 linhas afetadas. Verifique RLS ou ID:', sessionId)
      } else {
        logger.log('✅ [background] Dados básicos salvos. Linhas afetadas:', count)
      }



      // Coletar IP e geolocalização apenas se habilitado
      if (enableGeodata) {
        // Tentar obter IP (pode falhar)
        let visitorIP: string | undefined
        try {
          visitorIP = await getVisitorIP()
          if (visitorIP) {
            logger.log('✅ [background] IP obtido:', visitorIP)
            await publicSupabase
              .from('quiz_sessions')
              .update({ ip_address: visitorIP })
              .eq('id', sessionId)
          }
        } catch (ipErr) {
          logger.warn('⚠️ [background] Falha ao obter IP:', ipErr)
        }

        // Tentar obter geolocalização (pode falhar)
        try {
          const geoLocation = await getGeoLocation()
          if (geoLocation.country || geoLocation.region || geoLocation.city) {
            logger.log('✅ [background] Geolocalização obtida:', geoLocation)
            await publicSupabase
              .from('quiz_sessions')
              .update({
                country: geoLocation.country || null,
                region: geoLocation.region || null,
                city: geoLocation.city || null
              })
              .eq('id', sessionId)
          }
        } catch (geoErr) {
          logger.warn('⚠️ [background] Falha na geolocalização:', geoErr)
        }
      } else {
        logger.log('⏭️ [background] Pulando coleta de IP e geolocalização (VITE_ENABLE_GEODATA=false)')
      }

      logger.log('🎉 [background] Atualização em background concluída!')

    } catch (err) {
      logger.error('💥 [background] Erro na atualização em background:', err)
    }
  }

  const loadQuiz = async () => {
    logger.log('🚀 [loadQuiz] Iniciando carregamento do quiz...')
    const startTime = performance.now()

    try {
      setLoading(true)
      setError(null)
      setQuiz(null) // Limpa quiz anterior para evitar flash de conteúdo antigo


      logger.log('✅ [loadQuiz] Sessão permitida, continuando...')



      // Coletar dados síncronos PRIMEIRO (não precisa await)
      logger.log('📋 [loadQuiz] Coletando dados de contexto (síncrono)...')
      const host = getCurrentHost()
      const slug = getSlugFromPath()
      let capturedUtmParams = {}
      let capturedFacebookParams = {}

      try {
        capturedUtmParams = getUTMParameters()
      } catch (e) {
        logger.warn('⚠️ [loadQuiz] Erro ao capturar UTMs:', e)
      }

      try {
        capturedFacebookParams = getFacebookParameters()
      } catch (e) {
        logger.warn('⚠️ [loadQuiz] Erro ao capturar Facebook Params:', e)
      }
      const referrer = getReferrer()
      const currentPath = window.location.pathname
      const startedAtTimestamp = Date.now()

      logger.log('📍 [loadQuiz] Dados coletados:', {
        host,
        slug,
        utmParams: capturedUtmParams,
        facebookParams: capturedFacebookParams
      })

      // Armazenar UTMs e Facebook params para usar em redirecionamentos
      setUtmParams(capturedUtmParams)
      setFacebookParams(capturedFacebookParams)

      // Determinar qual RPC usar baseado no tipo de acesso
      // Se temos userSubdomain, estamos no domínio principal (quiz.accesso.pro/{subdomain}/{slug})
      // Se não, estamos em um domínio personalizado (meudominio.com/{slug})
      const userSubdomain = getUserSubdomainFromPath()

      logger.log('📡 [loadQuiz] Buscando quiz publicado...', { userSubdomain, host, slug })

      let publishedQuiz
      if (userSubdomain) {
        // Domínio principal: usar RPC que busca por subdomain do usuário
        logger.log('🏠 [loadQuiz] Usando RPC get_quiz_by_user_subdomain (domínio principal)')
        publishedQuiz = await supabaseRPC.getQuizByUserSubdomain(userSubdomain, slug, publicSupabase)
      } else {
        // Domínio personalizado: usar RPC que busca por hostname
        logger.log('🌐 [loadQuiz] Usando RPC get_published_quiz (domínio personalizado)')
        publishedQuiz = await supabaseRPC.getPublishedQuiz(host, slug, publicSupabase)
      }

      logger.log('📊 [loadQuiz] Quiz encontrado:', publishedQuiz)

      // Se não encontrou o quiz, apenas retornamos. O estado 'quiz' continuará null.
      // O componente QuizViewer vai renderizar a tela de 404 baseada nisso.
      if (!publishedQuiz || !publishedQuiz.id) {
        logger.log('❌ [loadQuiz] Quiz não encontrado ou não tem ID')
        setLoading(false)
        return
      }
      logger.log('✅ [loadQuiz] Quiz válido encontrado')

      setQuiz(publishedQuiz)

      logger.log('🤖 [loadQuiz] Verificando se deve pular sessão (bots)...')
      // Verificar se deve pular a sessão (robôs, crawlers, etc.)
      if (shouldSkipSession()) {
        logger.log('🚫 [loadQuiz] Sessão pulada: detecção de robô ou crawler')
        setLoading(false)
        return
      }

      // Verificar se está em modo preview (via query param ?preview=true)
      const urlParams = new URLSearchParams(window.location.search)
      const isPreviewMode = urlParams.get('preview') === 'true'
      if (isPreviewMode) {
        logger.log('👁️ [loadQuiz] Modo preview ativo - sessão NÃO será criada para não poluir estatísticas')
        setLoading(false)
        return
      }

      logger.log('🎯 [loadQuiz] Gerenciando sessão...')

      // Configurações de recuperação de sessão (Defaults: Reset a cada acesso, Janela de 1 dia para resetar contador)
      const sessionConfig = publishedQuiz.quiz_data?.config?.session || { mode: 'reset', durationDays: 1 }
      const mode = sessionConfig.mode || 'reset'
      const durationDays = sessionConfig.durationDays ?? 1 // Se undefined, 1 dia

      // Tentar recuperar sessão anterior do localStorage
      const storageKeySession = `quiz_session_${publishedQuiz.id}`
      let storedSessionId: string | null = null
      try {
        storedSessionId = localStorage.getItem(storageKeySession)
      } catch (e) {
        logger.warn('⚠️ [loadQuiz] Erro ao ler localStorage:', e)
      }

      let activeSession: QuizSession | null = null
      let shouldRestoreState = false

      if (storedSessionId) {
        logger.log('🔄 [loadQuiz] Verificando sessão existente:', storedSessionId)

        // Buscar sessão existente no banco
        const { data: existingSession, error: fetchError } = await publicSupabase
          .from('quiz_sessions')
          .select('*')
          .eq('id', storedSessionId)
          .maybeSingle()

        if (!fetchError && existingSession) {
          // Analisar idade da sessão
          const lastActivity = new Date(existingSession.updated_at || existingSession.created_at).getTime()
          const now = Date.now()
          const minutesInactive = (now - lastActivity) / (1000 * 60)
          const daysInactive = minutesInactive / (60 * 24)

          logger.log('⏱️ [loadQuiz] Análise de sessão:', {
            id: existingSession.id,
            daysInactive: daysInactive.toFixed(4),
            minutesInactive: minutesInactive.toFixed(1),
            config: { mode, durationDays },
            status: existingSession.status
          })

          // Regra Suprema:
          // Se estiver DENTRO da janela de tempo: REUSA a sessão (não conta view nova no dashboard)
          // Se estiver FORA da janela: CRIA NOVA sessão (conta view nova)
          if (daysInactive <= durationDays) {
            logger.log('✅ [loadQuiz] Dentro da janela de tempo. Reutilizando sessão (sem nova view).')
            activeSession = existingSession as QuizSession

            // Decidir se restaura o progresso (estado) ou reinicia
            if (mode === 'recover') {
              const isCompleted = existingSession.status === 'completed' || !!existingSession.completed_at

              if (isCompleted) {
                shouldRestoreState = true
                logger.log('🔄 [loadQuiz] Modo RECOVER: Sessão concluída. Restaurando para exibir última página.')
              } else {
                shouldRestoreState = false
                logger.log('⏮️ [loadQuiz] Modo RECOVER: Sessão não concluída. Reiniciando (nova regra: parcial não recupera).')
              }
            } else {
              // Modo RESET:
              // Se a sessão anterior já estiver completada, CRIAR NOVA SESSÃO.
              // Não queremos misturar dados novos com uma sessão finalizada, e o usuário pediu reset.
              const isCompleted = existingSession.status === 'completed' || !!existingSession.completed_at

              if (isCompleted) {
                logger.log('🚫 [loadQuiz] Modo RESET + Sessão Completa: Forçando NOVA sessão para não alterar dados antigos.')
                activeSession = null // Forçar criação de nova sessão abaixo
                localStorage.removeItem(storageKeySession)
              } else {
                // Se não completou (abandonou), reutiliza ID mas reseta estado visual (reinicia da capa)
                shouldRestoreState = false
                logger.log('⏮️ [loadQuiz] Modo RESET: Sessão incompleta reutilizada, reiniciando da capa.')
              }
            }
          } else {
            logger.log('🚫 [loadQuiz] Sessão expirada (> durationDays). Criando nova (nova view).')
            // Deixa activeSession null para criar uma nova abaixo
            localStorage.removeItem(storageKeySession)
          }
        } else {
          logger.warn('⚠️ [loadQuiz] Sessão armazenada não encontrada no banco.')
          localStorage.removeItem(storageKeySession)
        }
      }

      // Se não tem sessão válida (primeiro acesso ou expirou), cria nova
      if (!activeSession) {
        logger.log('🆕 [loadQuiz] Criando NOVA sessão...')
        const sessionPayload = {
          metadata: {
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
            host,
            slug,
            path: currentPath,
            utm: capturedUtmParams,
            referrer
          }
        };

        activeSession = await supabaseRPC.createQuizSession(publishedQuiz.id, sessionPayload)

        // Salvar ID no localStorage para persistência futura
        try {
          localStorage.setItem(storageKeySession, activeSession.id)
        } catch (e) {
          logger.warn('⚠️ [loadQuiz] Erro ao salvar sessionId no storage:', e)
        }

        logger.log('✅ [loadQuiz] Nova sessão criada:', activeSession.id)
      }

      const newSession = activeSession

      // Salvar started_at no localStorage (não precisa await - operação síncrona)
      const localStorageKey = `quiz_session_started_${newSession.id}`
      try {
        // Só sobrescreve se não existir (preserva o tempo original em caso de retomada)
        if (!localStorage.getItem(localStorageKey)) {
          localStorage.setItem(localStorageKey, startedAtTimestamp.toString())
        }
        sessionStartTimeRef.current = startedAtTimestamp
      } catch (err) {
        logger.warn('⚠️ [loadQuiz] Erro ao salvar started_at no localStorage:', err)
      }

      // Executar obtenção de dados adicionais em BACKGROUND
      updateSessionWithAdditionalData(newSession.id, currentPath, capturedUtmParams, referrer)

      setSession(newSession)

      // Carregar dados (Estado) APENAS se for uma sessão recuperada com modo 'recover'
      // Se for nova, ou se for 'reset', esses dados ficam vazios (estado inicial)
      // 1. Sempre tentar restaurar o histórico se existir (Preservação de Estatísticas)
      // Isso garante que o modo RESET (reuso de sessão) não zere a contagem de views das páginas anteriores
      const sessionData = (newSession as any).session_data || {}
      const pageHistoryData = sessionData.page_history || (newSession as any).page_history

      if (pageHistoryData && Array.isArray(pageHistoryData) && pageHistoryData.length > 0) {
        logger.log('📚 [loadQuiz] Histórico preservado/restaurado:', pageHistoryData.length, 'entradas')
        pageHistoryRef.current = pageHistoryData
        setPageHistory(pageHistoryData)
      } else {
        // Se não tem histórico (Sessão Nova ou Nunca iniciada), garante limpo
        logger.log('✨ [loadQuiz] Sem histórico anterior. Iniciando limpo.')
        pageHistoryRef.current = []
        setPageHistory([])
        pageViewStartTime.current = Date.now()
      }

      // 2. Restaurar Estado (Respostas, Leads, Score, Posição) APENAS se shouldRestoreState (Recover)
      if (shouldRestoreState) {
        // Priorizar colunas dedicadas (answers, leads) sobre session_data
        const answersData = (newSession as any).answers || sessionData.answers
        const leadsData = (newSession as any).leads || (newSession as any).lead || sessionData.leads || sessionData.lead
        const savedScore = sessionData.score || 0
        const savedAwardedSteps = sessionData.awarded_steps || []

        // Restaurar Gamificação
        if (savedScore > 0) {
          logger.log('🎮 [loadQuiz] Restaurando score:', savedScore)
          setScore(savedScore)
        }
        if (savedAwardedSteps.length > 0) {
          logger.log('🎮 [loadQuiz] Restaurando rewarded steps:', savedAwardedSteps)
          setAwardedSteps(savedAwardedSteps)
        }

        // Restaurar Respostas
        if (answersData && Object.keys(answersData).length > 0) {
          logger.log('📝 [loadQuiz] Restaurando respostas...')
          const pagesMap = new Map()
          if (publishedQuiz.quiz_data?.pages) {
            publishedQuiz.quiz_data.pages.forEach((page: any) => {
              const questionId = getQuestionId(page)
              pagesMap.set(questionId, page)
            })
          }

          const convertedAnswers: Record<string, any> = {}
          for (const [key, answer] of Object.entries(answersData)) {
            const answerObj = answer as any
            if (answerObj?.optionIds !== undefined) {
              convertedAnswers[key] = { ...answerObj, value: answerObj.optionIds }
              continue
            }
            // Se value é ID ou array de IDs, usar diretamente
            const value = answerObj?.value
            if (typeof value === 'string' && value.startsWith('id_')) {
              convertedAnswers[key] = answerObj
              continue
            }
            if (Array.isArray(value) && value.every((v: any) => typeof v === 'string' && v.startsWith('id_'))) {
              convertedAnswers[key] = answerObj
              continue
            }

            // Conversão de texto para ID (compatibilidade) - usar mapa para lookup rápido
            const page = pagesMap.get(key)
            if (page) {
              const optionsElement = page.elements?.find((el: any) => el.type === 'Options')
              if (optionsElement?.options) {
                if (typeof value === 'string') {
                  const option = optionsElement.options.find((opt: any) => opt.text === value)
                  if (option) {
                    convertedAnswers[key] = { ...answerObj, value: option.id }
                    continue
                  }
                } else if (Array.isArray(value)) {
                  const convertedIds = value.map((text: string) => {
                    if (text.startsWith('id_')) return text
                    const option = optionsElement.options.find((opt: any) => opt.text === text)
                    return option?.id || text
                  })
                  convertedAnswers[key] = { ...answerObj, value: convertedIds }
                  continue
                }
              }
            }

            // Fallback: manter como está
            convertedAnswers[key] = answerObj
          }
          setAnswers(convertedAnswers)
          setAnswersForDB(answersData as Record<string, any>)
          logger.log('✅ [loadQuiz] Respostas processadas:', Object.keys(convertedAnswers).length)
        } else {
          setAnswersForDB({})
        }

        // Restaurar Leads
        if (leadsData) {
          logger.log('👤 [loadQuiz] Carregando leads:', leadsData)
          setLeadData(leadsData)
        }

        // Restaurar Página Atual (Apenas Recover)
        // Restaurar Página Atual (Apenas Recover/Completed)
        // Se estamos restaurando (shouldRestoreState=true), sabemos pela nova regra que a sessão está completa.
        // Portanto, devemos exibir a última página.
        const totalPages = publishedQuiz.quiz_data?.pages?.length || 0
        if (totalPages > 0) {
          const lastPageIndex = totalPages - 1
          logger.log('🏁 [loadQuiz] Sessão completa restaurada: Exibindo última página (index', lastPageIndex, ')')
          setCurrentStep(lastPageIndex)
          setIsRecoveredCompleted(true)
        } else if (newSession.current_page_index && newSession.current_page_index > 0) {
          // Fallback caso não tenha pages (improvável)
          logger.log('📄 [loadQuiz] Restaurando progresso na página salva:', newSession.current_page_index)
          setCurrentStep(newSession.current_page_index)
        }
      } else {
        // Modo RESET ou NOVA Sessão (Se for NOVA, activeSession já veio vazia, mas garantimos aqui)
        // Se for RESET (reuso), limpamos os dados de progresso mas MANTIVEMOS o pageHistory acima.
        logger.log('✨ [loadQuiz] Modo RESET ou Nova Sessão: Limpando progresso (respostas/score), mantendo histórico se existir.')

        setAnswersForDB({})
        setAnswers({})
        setLeadData({})
        setScore(0)
        setAwardedSteps([])
        setCurrentStep(0)
      }

      const endTime = performance.now()
      const loadTime = Math.round(endTime - startTime)
      logger.log(`🎉 [loadQuiz] Quiz carregado com sucesso em ${loadTime}ms!`)

    } catch (err) {
      logger.error('💥 [loadQuiz] Erro durante carregamento:', err)
      setError(err instanceof Error ? err.message : 'Erro ao carregar quiz')
    } finally {
      logger.log('🏁 [loadQuiz] Finalizando carregamento...')
      setLoading(false)
    }
  }

  // Função helper para atualizar total_time_seconds a cada interação
  const updateTotalTime = async (overrideStep?: number) => {
    if (!session?.id) {
      logger.log('⚠️ [updateTotalTime] Sem sessão, pulando atualização de tempo')
      return
    }

    try {
      // Tentar obter started_at do localStorage primeiro (mais confiável e rápido)
      const localStorageKey = `quiz_session_started_${session.id}`
      let startedAtMs: number | null = null

      try {
        const storedValue = localStorage.getItem(localStorageKey)
        if (storedValue) {
          // Tentar parsear como número (milissegundos)
          const parsed = parseInt(storedValue, 10)
          if (!isNaN(parsed) && parsed > 0) {
            startedAtMs = parsed
            logger.log('💾 [updateTotalTime] Usando started_at do localStorage (ms):', startedAtMs)
          } else {
            // Fallback: tentar parsear como ISO string (compatibilidade com versões antigas)
            const dateMs = new Date(storedValue).getTime()
            if (!isNaN(dateMs) && dateMs > 0) {
              startedAtMs = dateMs
              logger.log('💾 [updateTotalTime] Convertendo ISO string do localStorage para ms:', startedAtMs)
              // Atualizar para formato numérico
              localStorage.setItem(localStorageKey, startedAtMs.toString())
            }
          }
        }
      } catch (err) {
        logger.warn('⚠️ [updateTotalTime] Erro ao ler localStorage:', err)
      }

      // Fallback 2: usar ref em memória (mais confiável que session.started_at do banco)
      if (!startedAtMs && sessionStartTimeRef.current) {
        startedAtMs = sessionStartTimeRef.current
        logger.log('💾 [updateTotalTime] Usando started_at do ref em memória (ms):', startedAtMs)
      }

      // Fallback 3: usar started_at da sessão se não estiver no localStorage nem no ref
      if (!startedAtMs && session.started_at) {
        const dateMs = new Date(session.started_at).getTime()
        if (!isNaN(dateMs) && dateMs > 0) {
          startedAtMs = dateMs
          logger.log('💾 [updateTotalTime] Usando started_at da sessão (ms):', startedAtMs)
          // Salvar no localStorage para próximas chamadas (em formato numérico)
          try {
            localStorage.setItem(localStorageKey, startedAtMs.toString())
          } catch (err) {
            logger.warn('⚠️ [updateTotalTime] Erro ao salvar started_at no localStorage:', err)
          }
        }
      }

      if (!startedAtMs) {
        logger.log('⚠️ [updateTotalTime] Sem started_at disponível, pulando atualização de tempo')
        return
      }

      // Calcular diferença em segundos (agora ambos são milissegundos)
      const timeSpent = Math.floor((Date.now() - startedAtMs) / 1000)
      logger.log('⏱️ [updateTotalTime] Atualizando tempo total:', timeSpent, 'segundos')

      // Usar o passo fornecido ou o atual do estado
      const stepToSave = overrideStep !== undefined ? overrideStep : currentStep

      await supabaseRPC.updateSessionProgress(session.id, {
        timeSpent,
        currentPageIndex: stepToSave
      }, publicSupabase)

      logger.log('✅ [updateTotalTime] Tempo total atualizado com sucesso. Passo salvo:', stepToSave)
    } catch (err) {
      logger.error('❌ [updateTotalTime] Erro ao atualizar tempo total:', err)
      // Não lançar erro para não interromper o fluxo principal
    }
  }

  const submitAnswer = async (questionId: string, answerData: any) => {
    logger.log('📝 [submitAnswer] Salvando resposta:', { questionId, answerData, currentStep })

    // --- Atualizar estado UI SEMPRE (mesmo sem sessão, ex: preview) ---
    try {
      const answerValue = answerData?.value !== undefined ? answerData.value : answerData
      const optionIds = answerData?.optionIds // IDs para UI
      const metadata = answerData?.value !== undefined ? {
        customId: answerData.customId,
        internalId: answerData.internalId
      } : null

      // Para o estado interno (UI), usar os IDs se disponíveis, senão usar o valor
      // Isso garante que o ElementRenderer possa comparar corretamente com option.id
      const uiValue = optionIds !== undefined ? optionIds : answerValue

      // Armazenar resposta com metadados no estado interno (usando IDs para UI)
      const fullAnswerForUI = metadata ? {
        value: uiValue, // IDs para controle visual
        ...metadata
      } : uiValue

      // Atualizar estado interno com IDs (para UI funcionar)

      const newAnswers = { ...answers, [questionId]: fullAnswerForUI }
      setAnswers(newAnswers)

      // Para o banco, salvar AMBOS texto e IDs (para não perder nenhuma informação)
      const fullAnswerForDB = metadata ? {
        value: answerValue, // Textos para análise
        optionIds: optionIds || answerValue, // IDs para UI (fallback para valor se não tiver IDs)
        ...metadata
      } : (optionIds ? { value: answerValue, optionIds } : answerValue)

      // Atualizar estado completo para banco (preservando todas as respostas anteriores)
      const newAnswersForDB = { ...answersForDB, [questionId]: fullAnswerForDB }
      setAnswersForDB(newAnswersForDB)

      if (!session) {
        logger.log('⚠️ [submitAnswer] Sem sessão ativa - UI atualizada, banco ignorado')
        return
      }

      // Atualizar sessão no banco - salvar answers APENAS na coluna dedicada
      const { error: updateError } = await publicSupabase
        .from('quiz_sessions')
        .update({
          answers: newAnswersForDB, // Coluna dedicada
          updated_at: new Date().toISOString()
        })
        .eq('id', session.id)

      if (updateError) {
        logger.error('❌ [submitAnswer] Erro ao atualizar respostas no banco:', updateError)
        throw updateError
      }

      // Atualizar tempo total após salvar resposta
      await updateTotalTime()

      logger.log('✅ [submitAnswer] Resposta salva com sucesso')
    } catch (err) {
      logger.error('❌ [submitAnswer] Erro ao salvar resposta:', err)
      throw err
    }
  }

  const nextStep = async () => {
    const totalSteps = quiz?.quiz_data?.pages?.length || 0
    if (currentStep < totalSteps - 1) {
      const newStep = currentStep + 1
      logger.log('➡️ [nextStep] Mudando para página:', newStep)
      setCurrentStep(newStep)

      // Executar operações de banco em background para não bloquear a UI
      const backgroundUpdates = async () => {
        try {
          // Registrar conclusão da página ANTERIOR (currentStep original)
          if (quiz && quiz.quiz_data?.pages?.[currentStep] && session?.id) {
            const currentPage = quiz.quiz_data.pages[currentStep]
            const pageId = getPageTrackingId(currentPage)
            logger.log('✅ [nextStep] Concluindo página anterior (background):', { pageIndex: currentStep, pageId })
            await trackPageCompletion(currentPage, session.id)
          }

          // Persistir progresso da nova página e atualizar tempo total
          if (session) {
            logger.log('💾 [nextStep] Salvando progresso no banco (background), página:', newStep)
            await updateTotalTime(newStep)
          }
        } catch (err) {
          logger.error('❌ [nextStep] Erro nas atualizações em background:', err)
        }
      }

      // Disparar sem await
      backgroundUpdates()
    }
  }

  const prevStep = async () => {
    if (currentStep > 0) {
      const newStep = currentStep - 1
      logger.log('⬅️ [prevStep] Voltando para página:', newStep)
      setCurrentStep(newStep)

      // Persistir progresso da página e atualizar tempo total
      if (session) {
        try {
          // Atualizar tempo total quando volta de página também
          await updateTotalTime(newStep)
        } catch (err) {
          logger.error('Erro ao salvar progresso:', err)
        }
      }
    }
  }

  const submitLead = async (data: Record<string, any>) => {
    logger.log('👤 [submitLead] Salvando dados de lead:', data)
    if (!session) {
      logger.log('⚠️ [submitLead] Sem sessão ativa')
      return
    }

    try {
      // Processar dados estruturados (de formulários) vs dados simples
      let processedData: Record<string, any> = {}

      // Se os dados vêm de formulários (têm estrutura especial)
      const hasStructuredData = Object.values(data).some((value: any) =>
        value && typeof value === 'object' && 'value' in value
      )

      if (hasStructuredData) {
        logger.log('🔄 [submitLead] Processando dados estruturados de formulário')

        // Preservar estrutura completa (com metadados) para o banco
        Object.entries(data).forEach(([key, fieldData]: [string, any]) => {
          if (fieldData && typeof fieldData === 'object' && 'value' in fieldData) {
            // Garantir estrutura unificada (sem fieldType, com optionIds se houver)
            const { value, customId, internalId, optionIds } = fieldData
            processedData[key] = {
              value,
              customId: customId || key,
              internalId: internalId || key,
              ...(optionIds ? { optionIds } : {})
            }
          } else {
            // Converter dados simples para estrutura unificada
            processedData[key] = {
              value: fieldData,
              customId: key,
              internalId: key
            }
          }
        })
      } else {
        // Dados simples
        processedData = data
      }

      // Deduplicar processedData: remover entrada com chave = internalId se existir outra entrada para o mesmo internalId
      Object.keys(processedData).forEach(key => {
        const item = processedData[key]
        if (item && typeof item === 'object' && item.internalId) {
          if (key === item.internalId) {
            // Esta é a entrada "técnica" (chave = ID interno)
            // Verificar se existe outra entrada "amigável" (chave != ID interno) para o mesmo item
            const hasFriendlyEntry = Object.keys(processedData).some(otherKey =>
              otherKey !== key &&
              processedData[otherKey]?.internalId === item.internalId
            )

            if (hasFriendlyEntry) {
              delete processedData[key]
            }
          }
        }
      })

      logger.log('📊 [submitLead] Dados processados para banco:', processedData)

      // Mesclar com leads anteriores, removendo duplicatas por internalId
      const mergedLeadData = { ...leadData }

      // Remover entradas que usam internalId como chave se já existe uma entrada com o mesmo customId
      Object.keys(processedData).forEach(customId => {
        const fieldData = processedData[customId]
        if (fieldData && typeof fieldData === 'object' && 'internalId' in fieldData) {
          const internalId = fieldData.internalId

          // Se customId é diferente de internalId, remover entrada duplicada com internalId como chave
          if (customId !== internalId && mergedLeadData[internalId]) {
            delete mergedLeadData[internalId]
          }
        }
      })

      // Agora mesclar os novos dados
      Object.assign(mergedLeadData, processedData)
      setLeadData(mergedLeadData)

      // Se a sessão já estiver completa, NÃO atualizar o banco
      if (session.status === 'completed' || session.completed_at) {
        logger.log('🛑 [submitLead] Sessão já completada, ignorando salvamento no banco.')
        return
      }

      // Atualizar sessão no banco - salvar lead APENAS na coluna dedicada (NÃO em session_data)
      logger.log('💾 [submitLead] Atualizando sessão no banco...')
      const { error: updateError } = await publicSupabase
        .from('quiz_sessions')
        .update({
          leads: mergedLeadData,  // Coluna dedicada
          updated_at: new Date().toISOString()
        })
        .eq('id', session.id)

      if (updateError) {
        logger.error('❌ [submitLead] Erro ao atualizar sessão:', updateError)
        throw updateError
      }
      // page_history é salvo automaticamente em session_data pelas funções de tracking
      // answers e lead estão APENAS nas colunas dedicadas, não em session_data

      logger.log('✅ [submitLead] Lead salvo com sucesso')
    } catch (err) {
      logger.error('❌ [submitLead] Erro ao salvar lead:', err)
      throw err
    }
  }

  const saveScore = async (newScore: number, newAwardedSteps: number[]) => {
    if (!session?.id) return

    setScore(newScore)
    setAwardedSteps(newAwardedSteps)

    try {
      // Buscar session_data atual
      const { data: currentSession } = await publicSupabase
        .from('quiz_sessions')
        .select('session_data')
        .eq('id', session.id)
        .single()

      const currentSessionData = currentSession?.session_data || {}
      const updatedSessionData = {
        ...currentSessionData,
        score: newScore,
        awarded_steps: newAwardedSteps
      }

      await publicSupabase
        .from('quiz_sessions')
        .update({ session_data: updatedSessionData })
        .eq('id', session.id)

      logger.log('💾 [saveScore] Score salvo:', newScore)
    } catch (err) {
      logger.error('❌ [saveScore] Erro ao salvar score:', err)
    }
  }

  const completeQuiz = async (formData?: Record<string, any>) => {
    logger.log('🏁 [completeQuiz] Iniciando conclusão do quiz...', { formData })
    if (!session) {
      logger.log('⚠️ [completeQuiz] Sem sessão ativa')
      return
    }

    // Se a sessão já estiver completa, NÃO fazer nada (manter tudo congelado)
    if (session.status === 'completed' || session.completed_at) {
      logger.log('🛑 [completeQuiz] Sessão já completada, ignorando nova conclusão.')
      return
    }

    try {
      // IMPORTANTE: Registrar a visualização da página final ANTES de marcar como completo
      // Isso garante que a última página seja contabilizada nas estatísticas

      // Aguardar um pequeno delay para dar chance ao useEffect de trackPageView executar
      // (complementar ao delay do QuizViewer)
      await new Promise(resolve => setTimeout(resolve, 100))

      // IMPORTANTE: Garantir que a última página está no histórico
      // Usamos uma lógica manual aqui para não depender do useEffect (race condition)
      if (quiz && quiz.quiz_data?.pages?.[currentStep]) {
        const finalPage = quiz.quiz_data.pages[currentStep]
        const finalPageId = getPageTrackingId(finalPage)

        // Verificar se já existe no histórico ATUAL (ref)
        const currentHistory = [...pageHistoryRef.current]
        const existingIndex = currentHistory.findIndex(p => p.pageId === finalPageId)

        logger.log('📊 [completeQuiz] Verificando página final:', {
          pageIndex: currentStep,
          pageId: finalPageId,
          existsInHistory: existingIndex >= 0,
          currentHistoryLength: currentHistory.length,
          currentHistoryPages: currentHistory.map(p => ({ pageId: p.pageId, pageIndex: p.pageIndex }))
        })

        // Se NÃO existe, adicionar manualmente
        if (existingIndex === -1) {
          logger.log('📊 [completeQuiz] ⚠️ Página final faltando no histórico! Adicionando manualmente:', {
            pageIndex: currentStep,
            pageId: finalPageId
          })

          const finalEntry = {
            pageId: finalPageId,
            pageIndex: currentStep,
            viewedAt: nowGmtMinus3ISO()
          }

          // Adicionar ao histórico local
          const updatedHistory = [...currentHistory, finalEntry]
          pageHistoryRef.current = updatedHistory
          setPageHistory(updatedHistory)

          // Forçar salvamento via fila e AGUARDAR
          logger.log('📊 [completeQuiz] Salvando histórico atualizado com página final...')
          await savePageHistoryToDatabase(updatedHistory, session.id)

          // LOG CONCLUSIVO - ADIÇÃO MANUAL (apenas em dev)
          if (import.meta.env.DEV) {
            console.log('%c═══════════════════════════════════════════════════════════════', 'color: #f59e0b; font-weight: bold')
            console.log('%c🔶 ÚLTIMA PÁGINA: Adicionada MANUALMENTE ao histórico', 'color: #f59e0b; font-weight: bold; font-size: 14px')
            console.log('%c   PageId: ' + finalPageId, 'color: #f59e0b')
            console.log('%c   PageIndex: ' + currentStep, 'color: #f59e0b')
            console.log('%c   Histórico final: ' + updatedHistory.length + ' páginas', 'color: #f59e0b')
            console.log('%c   Status: ✅ SALVO NO BANCO', 'color: #22c55e; font-weight: bold')
            console.log('%c═══════════════════════════════════════════════════════════════', 'color: #f59e0b; font-weight: bold')
          }
        } else {
          // Se já existe, apenas garantir que está salvo (flush da fila)
          await savePageHistoryToDatabase(currentHistory, session.id)

          // LOG CONCLUSIVO - JÁ EXISTIA (apenas em dev)
          if (import.meta.env.DEV) {
            console.log('%c═══════════════════════════════════════════════════════════════', 'color: #22c55e; font-weight: bold')
            console.log('%c✅ ÚLTIMA PÁGINA: Já estava no histórico (trackPageView funcionou)', 'color: #22c55e; font-weight: bold; font-size: 14px')
            console.log('%c   PageId: ' + finalPageId, 'color: #22c55e')
            console.log('%c   PageIndex: ' + currentStep, 'color: #22c55e')
            console.log('%c   Posição no histórico: índice ' + existingIndex, 'color: #22c55e')
            console.log('%c   Histórico final: ' + currentHistory.length + ' páginas', 'color: #22c55e')
            console.log('%c   Status: ✅ CONFIRMADO NO BANCO', 'color: #22c55e; font-weight: bold')
            console.log('%c═══════════════════════════════════════════════════════════════', 'color: #22c55e; font-weight: bold')
          }
        }
      } else {
        // LOG DE ERRO (apenas em dev)
        if (import.meta.env.DEV) {
          console.log('%c═══════════════════════════════════════════════════════════════', 'color: #ef4444; font-weight: bold')
          console.log('%c❌ ÚLTIMA PÁGINA: ERRO - quiz ou pages não disponível!', 'color: #ef4444; font-weight: bold; font-size: 14px')
          console.log('%c   quiz: ' + (quiz ? 'OK' : 'NULL'), 'color: #ef4444')
          console.log('%c   pages: ' + (quiz?.quiz_data?.pages ? 'OK' : 'NULL'), 'color: #ef4444')
          console.log('%c   currentStep: ' + currentStep, 'color: #ef4444')
          console.log('%c   Status: ⚠️ NÃO FOI POSSÍVEL SALVAR', 'color: #ef4444; font-weight: bold')
          console.log('%c═══════════════════════════════════════════════════════════════', 'color: #ef4444; font-weight: bold')
        }
        logger.warn('⚠️ [completeQuiz] Não foi possível verificar página final - quiz ou pages não disponível')
      }

      // Calcular tempo total gasto (usar localStorage como fonte primária)
      const localStorageKey = `quiz_session_started_${session.id}`
      let startedAtMs: number | null = null

      try {
        const storedValue = localStorage.getItem(localStorageKey)
        if (storedValue) {
          // Tentar parsear como número (milissegundos)
          const parsed = parseInt(storedValue, 10)
          if (!isNaN(parsed) && parsed > 0) {
            startedAtMs = parsed
          } else {
            // Fallback: tentar parsear como ISO string
            const dateMs = new Date(storedValue).getTime()
            if (!isNaN(dateMs) && dateMs > 0) {
              startedAtMs = dateMs
            }
          }
        }
      } catch (err) {
        logger.warn('⚠️ [completeQuiz] Erro ao ler localStorage:', err)
      }

      // Fallback 2: usar ref em memória
      if (!startedAtMs && sessionStartTimeRef.current) {
        startedAtMs = sessionStartTimeRef.current
      }

      // Fallback 3: usar started_at da sessão
      if (!startedAtMs && session.started_at) {
        const dateMs = new Date(session.started_at).getTime()
        if (!isNaN(dateMs) && dateMs > 0) {
          startedAtMs = dateMs
        }
      }

      const timeSpent = startedAtMs
        ? Math.floor((Date.now() - startedAtMs) / 1000)
        : undefined

      logger.log('⏱️ [completeQuiz] Tempo calculado:', timeSpent, 'segundos')

      // Marcar quiz como completo e atualizar tempo total
      logger.log('💾 [completeQuiz] Marcando como completo no banco...')
      await supabaseRPC.updateSessionProgress(session.id, {
        completed: true,
        timeSpent,
        currentPageIndex: currentStep
      }, publicSupabase)

      // Garantir que o tempo total foi atualizado (já foi atualizado acima, mas garantimos)
      logger.log('✅ [completeQuiz] Tempo total atualizado na conclusão')

      // Atualizar completed_at com timestamp GMT-3 (a RPC usa now() que é GMT-0)
      const completedAtGmt3 = nowGmtMinus3ISO()
      logger.log('🕐 [completeQuiz] Atualizando completed_at para GMT-3:', completedAtGmt3)
      const { error: completedAtError } = await supabase
        .from('quiz_sessions')
        .update({ completed_at: completedAtGmt3 })
        .eq('id', session.id)

      if (completedAtError) {
        logger.error('❌ [completeQuiz] Erro ao atualizar completed_at:', completedAtError)
      } else {
        logger.log('✅ [completeQuiz] completed_at atualizado para GMT-3')
      }

      // Se temos dados de formulário, salvar como lead primeiro
      let finalLeadData = leadData
      if (formData && Object.keys(formData).length > 0) {
        logger.log('📝 [completeQuiz] Salvando dados de formulário como lead:', formData)

        // Processar dados do formulário com metadados estruturados
        const processedFormData: Record<string, any> = {}
        Object.entries(formData).forEach(([key, value]) => {
          // Verificar se o valor já é estruturado (vindo do ElementRenderer)
          if (value && typeof value === 'object' && 'value' in value) {
            const { value: v, customId, internalId, optionIds } = value as any
            processedFormData[key] = {
              value: v,
              customId: customId || key,
              internalId: internalId || key,
              ...(optionIds ? { optionIds } : {})
            }
          } else {
            // Fallback para dados simples
            processedFormData[key] = {
              value: value,
              customId: key,
              internalId: key
            }
          }
        })

        // Deduplicar processedFormData: remover entrada com chave = internalId se existir outra entrada para o mesmo internalId
        Object.keys(processedFormData).forEach(key => {
          const item = processedFormData[key]
          if (item && typeof item === 'object' && item.internalId) {
            if (key === item.internalId) {
              const hasFriendlyEntry = Object.keys(processedFormData).some(otherKey =>
                otherKey !== key &&
                processedFormData[otherKey]?.internalId === item.internalId
              )

              if (hasFriendlyEntry) {
                delete processedFormData[key]
              }
            }
          }
        })

        logger.log('🔄 [completeQuiz] Dados processados:', processedFormData)
        finalLeadData = { ...leadData, ...processedFormData }

        // Salvar como lead via RPC
        await submitLead(processedFormData)
      }

      // Garantir que page_history final está salvo (já foi salvo automaticamente, mas garantimos aqui também)
      // IMPORTANTE: Usar pageHistoryRef.current que é a fonte de verdade, não o estado React
      await savePageHistoryToDatabase(pageHistoryRef.current)

      // Buscar dados atualizados da sessão
      const { data: updatedSession, error: fetchError } = await supabase
        .from('quiz_sessions')
        .select('*')
        .eq('id', session.id)
        .single()

      if (fetchError) throw fetchError

      setSession(updatedSession)

      // Limpar started_at do localStorage após completar o quiz
      if (session.id) {
        const localStorageKey = `quiz_session_started_${session.id}`
        try {
          localStorage.removeItem(localStorageKey)
          logger.log('🗑️ [completeQuiz] started_at removido do localStorage')
        } catch (err) {
          logger.warn('⚠️ [completeQuiz] Erro ao remover started_at do localStorage:', err)
        }
      }

      logger.log('✅ [completeQuiz] Quiz completado com sucesso!')
    } catch (err) {
      logger.error('❌ [completeQuiz] Erro ao completar quiz:', err)
      throw err
    }
  }

  // Função helper para adicionar UTMs e parâmetros do Facebook a URLs de redirecionamento
  const addUTMsToRedirectUrl = (url: string): string => {
    return addTrackingParamsToUrl(url, {
      utm: utmParams,
      facebook: facebookParams
    })
  }

  useEffect(() => {
    logger.log('🔄 [useQuizViewer] Hook inicializado, chamando loadQuiz...')
    if (!initialized.current) {
      initialized.current = true
      loadQuiz()
    }
  }, [])

  // Rastrear visualização de páginas
  useEffect(() => {
    logger.log('👀 [PageTracker Effect] Triggered:', {
      hasQuiz: !!quiz,
      hasSession: !!session?.id,
      currentStep,
      pageExists: !!quiz?.quiz_data?.pages?.[currentStep]
    })

    if (quiz && quiz.quiz_data?.pages?.[currentStep] && session?.id) {
      const currentPage = quiz.quiz_data.pages[currentStep]
      const pageId = getPageTrackingId(currentPage)

      logger.log('👁️ [PageTracker Effect] Rastreando visualização de página:', {
        pageIndex: currentStep,
        pageId,
        sessionId: session.id,
        totalPages: quiz.quiz_data.pages.length
      })

      // Chamar de forma assíncrona para salvar imediatamente, passando sessionId
      trackPageView(currentPage, currentStep, session.id).catch(err => {
        logger.error('❌ [PageTracker Effect] Erro ao rastrear visualização de página:', err)
      })
    } else {
      if (!session?.id) {
        logger.warn('⚠️ [PageTracker Effect] Sem session.id, aguardando sessão...')
      } else if (!quiz) {
        logger.warn('⚠️ [PageTracker Effect] Sem quiz, aguardando quiz...')
      } else if (!quiz.quiz_data?.pages?.[currentStep]) {
        logger.error('❌ [PageTracker Effect] Página não encontrada para o índice:', currentStep)
      }
    }
  }, [currentStep, quiz, session])

  const trackExternalClick = async () => {
    if (!session?.id) return
    logger.log('🖱️ [trackExternalClick] Registrando clique externo...')
    try {
      await supabaseRPC.updateQuizSession(session.id, { has_external_click: true }, publicSupabase)
      logger.log('✅ [trackExternalClick] Clique registrado com sucesso')
    } catch (err) {
      logger.error('❌ [trackExternalClick] Erro ao registrar clique:', err)
    }
  }

  return {
    quiz,
    session,
    loading,
    error,
    currentStep,
    answers,
    leadData,
    pageHistory,
    submitAnswer,
    nextStep,
    prevStep,
    submitLead,
    completeQuiz,
    addUTMsToRedirectUrl,
    trackExternalClick,
    score,
    awardedSteps,
    saveScore,
    isRecoveredCompleted
  }
}